/* eslint-disable no-underscore-dangle, react/destructuring-assignment, no-plusplus */
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import DataTable from "src/Components/DataTable";
import DataLoading from "src/Components/Loading/dataLoading";
import { MAGENTO_ADMIN_SERVICE, restApiRequest } from "src/utils/Api";
import __ from "src/utils/Translations";
import FormTitle from "src/Components/Form/FormTitle";
import ActionColumn from "src/Components/DataTable/actionColumn";
import useProductSuppliers from "src/utils/hooks/suppliers/useProductSuppliers";
import {
  AsyncAutocompleteSelectFilter,
  DateFilter,
  dateFilterMethod,
  SelectFilter,
} from "src/Components/DataTable/filters";
import {
  getDateCell,
  mapValueFromOptions,
  priceColumn,
} from "src/Components/DataTable/commonCells";
import useCompanyPointsBanks from "src/utils/hooks/pointsBank/useCompanyPointsBanks";
import priceFormatter from "src/utils/jsHelpers/priceFormatter";
import { getSuppliersOptionsFetchMethod } from "src/Components/FormElements/Autocomplete/commonFetchMethods";
import { getListingData } from "src/Components/DataTableControlled/utils";
import ButtonsList from "src/Components/ButtonsList";
import useHasPermission from "src/utils/hooks/security/useHasPermission";
import { catalogManagementInactiveEmployeeCodesWrite } from "src/utils/RoleBasedSecurity/permissions";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { dynamicNotification } from "src/utils/Notifications";
import DataTableControlled from "src/Components/DataTableControlled";
import ContentLoading from "src/Components/Loading/contentLoading";
import Spinner from "src/Components/Spinner";
import sendBatchRequests from "src/utils/Api/sendBatchRequests";
import {
  clearOrderIdFromUrl,
  getOrderIdFromSearch,
} from "src/Pages/Company/EmployeeManagement/utils/searchEmployeeByOrderNumber";
import Popup, { popupVariants } from "./popup";
import PreviewItemPopup from "./previewItemPopup";
import getCodeItemStatus, {
  getOrderItemStatusFilterOptions,
} from "./utils/codeItemStatus";

const TOTAL_ITEMS_LIMIT = 100;

export default function OneTimeService({
  employeeId,
  companyId,
  isEmployeeActive,
  employeeEmails,
}) {
  const [tableData, setTableData] = useState([]);
  const [paymentMethodsMap, setPaymentMethodsMap] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [alreadyFetched, setAlreadyFetched] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupVariant, setPopupVariant] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [pagesNumberToRequest, setPagesNumberToRequest] = useState(0);
  const [hasCodes, setHasCodes] = useState(false);
  const handledOrderIdFromUrlRef = useRef(false);

  const history = useHistory();
  const location = useLocation();

  const closeForm = useCallback(() => {
    setOpenPopup(false);
    setPopupVariant(null);
  }, [setOpenPopup, setPopupVariant]);

  const suppliersOptions = useProductSuppliers(true, true);

  const isSendCodesToUserEnabled = useHasPermission(
    catalogManagementInactiveEmployeeCodesWrite
  );

  const pointsBanks = useCompanyPointsBanks(true, companyId, !companyId);

  const orderItemStatusFilterOptions = useMemo(
    () => getOrderItemStatusFilterOptions(),
    []
  );
  const orderStatusFilterOptions = useMemo(() => {
    const uniqueLabels = [
      ...new Set(
        tableData
          .map(({ orderStatusLabel }) => orderStatusLabel)
          .filter(Boolean)
      ),
    ];

    return uniqueLabels.map((label) => ({
      value: label,
      label,
    }));
  }, [tableData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsItemsLoading(true);

        // first request is for better experience
        const { data } = await getListingData(
          MAGENTO_ADMIN_SERVICE,
          "/user-orders",
          [{ id: "customerBusinessId", value: employeeId }],
          1,
          TOTAL_ITEMS_LIMIT,
          null,
          {}
        );

        parseTableData(data, setTableData, setAlreadyFetched);

        if (data?.length === TOTAL_ITEMS_LIMIT) {
          const newData = await sendBatchRequests({
            filterField: "customerBusinessId",
            filterValue: employeeId,
            requestParams: {
              service: MAGENTO_ADMIN_SERVICE,
              path: "/user-orders",
              method: "GET",
              itemsPerPage: TOTAL_ITEMS_LIMIT,
              startPage: 2,
            },
          });

          parseTableData(newData, setTableData, setAlreadyFetched);
        }

        setIsItemsLoading(false);
      } catch (error) {
        console.error(error);
        newRelicErrorReport(
          error,
          "Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OneTimeServices/index.js - 110"
        );
        dynamicNotification(
          error.mesage || __("Nie udało się pobrać listy."),
          "error"
        );
        setIsItemsLoading(false);
      }
    };

    fetchData();
  }, [employeeId, setTableData, setAlreadyFetched]);

  useEffect(() => {
    handledOrderIdFromUrlRef.current = false;
    setPreviewItem(null);
    setItemId(null);
  }, [employeeId]);

  useEffect(() => {
    if (handledOrderIdFromUrlRef.current || isItemsLoading || !alreadyFetched) {
      return;
    }

    const orderIdFromUrl = getOrderIdFromSearch(location.search);

    if (!orderIdFromUrl) {
      return;
    }

    handledOrderIdFromUrlRef.current = true;

    const matchingItems = tableData.filter(
      (item) => String(item.orderId) === String(orderIdFromUrl)
    );

    clearOrderIdFromUrl(history, location);

    if (matchingItems.length === 0) {
      dynamicNotification(
        __("Nie znaleziono zamówienia w liście świadczeń"),
        "error"
      );
      return;
    }

    if (matchingItems.length > 1) {
      return;
    }

    const [matchingItem] = matchingItems;
    setItemId(matchingItem.itemId);
    setPreviewItem(matchingItem);
  }, [
    alreadyFetched,
    isItemsLoading,
    tableData,
    location.search,
    history,
    location,
  ]);

  useEffect(() => {
    setPaymentMethods([...pointsBanks, ...staticMethods]);
  }, [pointsBanks]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!isSendCodesToUserEnabled || isEmployeeActive) {
          setHasCodes(false);
          return;
        }

        const { hasCodes } = (await restApiRequest(
          MAGENTO_ADMIN_SERVICE,
          `/get-mass-codes-data/${employeeId}/has-codes`,
          "GET",
          {},
          []
        )) || { hasCodes: false };

        setHasCodes(hasCodes);
      } catch (e) {
        console.error(e);
        newRelicErrorReport(
          e,
          "Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OneTimeServices/index.js - 115"
        );
        dynamicNotification(e.message || __("Błąd pobierania danych"), "error");
      }
    };

    fetchData();
  }, [employeeId, isEmployeeActive, isSendCodesToUserEnabled]);

  useEffect(() => {
    const newPaymentMethodsMap = {};
    paymentMethods.forEach(({ value, label }) => {
      newPaymentMethodsMap[value] = label;
    });
    setPaymentMethodsMap(newPaymentMethodsMap);
  }, [paymentMethods]);

  const buttons = useMemo(() => {
    const defaultButtons = [
      {
        size: "lg",
        color: "primary",
        className: "me-2",
        text: "Warunki edycji świadczeń jednorazowych",
        data1: "subscription-edit-rules",
        onClick: () => {
          setOpenPopup(true);
          setPopupVariant(popupVariants.oneTimeServiceEditInfo);
        },
      },
    ];

    if (isSendCodesToUserEnabled) {
      defaultButtons.push({
        size: "lg",
        color: "primary",
        className: "me-2",
        text: "Wyślij kody do użytkownika",
        disabled: !hasCodes || isEmployeeActive,
        onClick: () => {
          setOpenPopup(true);
          setPopupVariant(popupVariants.sendCodesToEmployee);
        },
      });
    }

    return defaultButtons;
  }, [isSendCodesToUserEnabled, isEmployeeActive, hasCodes, setOpenPopup]);

  return (
    <Card>
      <FormTitle
        title="Warunki edycji świadczeń jednorazowych"
        buttons={<ButtonsList buttons={buttons} />}
      />
      <CardBody>
        <ContentLoading
          message={Spinner}
          show={!tableData || isItemsLoading}
          useBlur={false}
          backgroundStyle={{
            zIndex: 1000,
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          }}
        >
          <DataTable
            id="oneTimeServicesListing"
            key="one_time_services_listing"
            data={tableData}
            noCards
            filterable
            isFiltersLoading={isItemsLoading}
            columns={[
              {
                Header: "SKU",
                accessor: "sku",
              },
              {
                Header: "Dostawca",
                accessor: "supplierId",
                Filter: AsyncAutocompleteSelectFilter((filterValue) =>
                  getSuppliersOptionsFetchMethod(filterValue)
                ),
                Cell: mapValueFromOptions(suppliersOptions, "supplierId"),
                sortable: false,
              },
              {
                Header: "Nazwa świadczenia",
                accessor: "name",
              },
              {
                Header: "Ogólny koszt świadczenia",
                accessor: "rowTotal",
                Cell: priceColumn,
              },
              {
                Header: "Sposób opłacenia",
                accessor: "payments",
                Filter: SelectFilter(paymentMethods),
                filterMethod: filterBanksItems,
                Cell: (rowData) =>
                  displayBanksItems(rowData.row._original, paymentMethodsMap),
              },
              {
                Header: "ID płatności Online",
                accessor: "paymentsOnline",
                Cell: (rowData) =>
                  displayPaymentMethodOnline(rowData.row._original),
              },
              {
                Header: "Zamówienie",
                accessor: "orderId",
              },
              {
                Header: "Status zamówienia",
                accessor: "orderStatusLabel",
                Filter: SelectFilter(orderStatusFilterOptions),
                Cell: mapValueFromOptions(
                  orderStatusFilterOptions,
                  "orderStatusLabel"
                ),
                minWidth: 180,
              },
              {
                Header: "Status kodu",
                accessor: "orderItemStatusLabel",
                Filter: SelectFilter(orderItemStatusFilterOptions),
                minWidth: 180,
              },
              {
                Header: "ID",
                accessor: "businessId",
              },
              {
                Header: "Data ostatniej płatności",
                accessor: "lastPaymentDate",
                Filter: DateFilter(),
                filterMethod: dateFilterMethod,
                Cell: getDateCell("lastPaymentDate"),
                minWidth: 150,
              },
              {
                Header: "Akcja",
                filterable: false,
                sortable: false,
                accessor: "action",
                Cell: (rowData) => (
                  <div className="d-block w-100 text-center">
                    <ActionColumn
                      data={rowData.row._original}
                      buttons={[
                        {
                          id: "cancelSubscription",
                          onClick: () => {},
                          label: "Anuluj",
                          disabled: true,
                        },
                        {
                          id: "showDetails",
                          onClick: () => {
                            setItemId(rowData?.original?.itemId);
                            setPreviewItem(rowData.row._original);
                          },
                          label: "Podgląd wartości z dnia zakupu",
                        },
                      ]}
                    />
                  </div>
                ),
              },
            ]}
          />
        </ContentLoading>
        {openPopup ? (
          <Popup
            close={closeForm}
            isOpen={Boolean(openPopup)}
            variant={popupVariant}
            employeeEmails={employeeEmails}
            employeeId={employeeId}
          />
        ) : null}
        {previewItem ? (
          <PreviewItemPopup
            close={() => setPreviewItem(null)}
            previewItem={previewItem}
            employeeId={employeeId}
            itemId={itemId}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

const parseTableData = (response, setTableData, setAlreadyFetched) => {
  let items = [];
  const supplierIds = [];

  response.forEach(
    ({
      items: orderItems,
      id: orderId,
      orderType,
      status: orderStatus,
      statusLabel: orderStatusLabel,
    }) => {
      items = [
        ...items,
        ...orderItems.map((item) => {
          let lastPaymentDate = null;
          const paymentOnlineIds = [];
          const { productId, productCodeBusinessId } = item;
          let businessId = `${productId}-${productCodeBusinessId}`;
          if (!productId) {
            businessId = `${productCodeBusinessId}`;
          }
          const payments = item?.paymentMethod?.reduce(
            (
              result,
              {
                transaction_id: transactionId,
                external_transaction_id: externalTransactionId,
                transaction_status: transactionStatus,
                created_at: createdAt,
                values,
              }
            ) => {
              const onlineTransactionId =
                externalTransactionId || transactionId || "";
              const paymentDate = new Date(createdAt);
              if (!lastPaymentDate || paymentDate > lastPaymentDate) {
                lastPaymentDate = paymentDate;
              }
              if (values.find(({ type }) => type === "ONLINE_PAYMENT")) {
                paymentOnlineIds.push(onlineTransactionId);
              }
              const statusValues = values.map((v) => ({
                ...v,
                transactionStatus,
                onlineTransactionId,
              }));
              result.push(...statusValues);

              return result;
            },
            []
          );
          const paymentsOnline = paymentOnlineIds.join();

          if (item?.supplierId) {
            supplierIds.push(item.supplierId?.toUpperCase());
          }

          const {
            statusCode: orderItemStatusCode,
            label: orderItemStatusLabel,
            canShowCode,
          } = getCodeItemStatus({
            productCodeStatus: item.productCodeStatus,
            orderStatus,
            orderType,
            productUserUsed: item.productUserUsed,
            productExpirationDate: item.productExpirationDate,
          });

          return {
            ...item,
            productCodeBusinessId,
            businessId,
            payments,
            paymentsOnline,
            lastPaymentDate,
            orderType,
            orderId,
            orderStatus,
            orderStatusLabel: orderStatusLabel || orderStatus,
            orderItemStatusCode,
            orderItemStatusLabel,
            canShowCode,
          };
        }),
      ];
    }
  );

  setTableData((prevData) => {
    if (items.length > 0) {
      return [...prevData, ...items].sort(
        (a, b) => new Date(b.lastPaymentDate) - new Date(a.lastPaymentDate)
      );
    }
    return prevData;
  });

  setAlreadyFetched(true);
};

const staticMethods = [
  { value: "payu_gateway", label: "Płatność online - PayU" },
  { value: "payu_gateway_blik", label: "Płatność online - Blik" },
];
const transactionStatusColors = {
  new: "text-secondary",
  success: "text-success",
  rolled_back: "text-danger",
  roll_back_error: "text-danger",
  to_cancel: "text-danger",
  canceled: "text-danger",
  error: "text-danger",
  rejected: "text-danger",
  error_respond: "text-danger",
};

const filterBanksItems = (filter, row) =>
  row[filter.id]?.find(({ code }) => code === filter.value);

const displayBanksItems = (data, paymentMethodsMap) => {
  const { payments } = data;
  if (!payments) {
    return null;
  }

  return (
    <ul>
      {payments.map(({ code, value }) => {
        const serviceName = paymentMethodsMap[code] || "Nieznany bank";
        const formattedPrice = priceFormatter(value);

        return (
          <li key={code} data-t1={serviceName} data-t2={formattedPrice}>
            {serviceName}: {formattedPrice}
          </li>
        );
      })}
    </ul>
  );
};

const displayPaymentMethodOnline = (data) => {
  const { payments } = data;
  if (!payments) {
    return null;
  }

  const onlinePayments = payments.filter(
    ({ type }) => type === "ONLINE_PAYMENT"
  );
  if (!onlinePayments) {
    return null;
  }

  return (
    <ul>
      {onlinePayments.map(({ onlineTransactionId, transactionStatus }) => (
        <li key={onlineTransactionId}>
          {transactionStatusColors[transactionStatus] ? (
            <span className={`${transactionStatusColors[transactionStatus]}`}>
              {onlineTransactionId}
            </span>
          ) : (
            onlineTransactionId
          )}
        </li>
      ))}
    </ul>
  );
};

OneTimeService.propTypes = {
  employeeId: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  isEmployeeActive: PropTypes.bool.isRequired,
  employeeEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
};
